import React, { useState, useMemo } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import {
  Eye,
  Database,
  CheckCircle,
  BarChart3,
  TrendingUp,
  Info,
} from "lucide-react";
import { useDataHistory, useMigrationHistorySummary } from "../../hooks";
import { DataHistoryItem } from "../../types";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PAGE_SIZE } from "@shared/utils/constants";
import { toast } from "@shared/hooks/use-toast";
import { PageHeader } from "@shared/components/PageHeader";

// Table columns configuration
const columns: TableColumn<DataHistoryItem>[] = [
  {
    key: "migrationName",
    header: "Migration Name",
    type: "text",
    render: (value) => (
      <span className="font-medium text-gray-900">{value}</span>
    ),
  },
  {
    key: "date",
    header: "Date",
    type: "text",
    render: (value) => <span className="text-gray-600">{value}</span>,
  },
  {
    key: "dataCategory",
    header: "Data Category",
  },
  {
    key: "statusDisplay",
    header: "Status",
  },
  {
    key: "totalRecords",
    header: "Total Records",
  },
  {
    key: "successRecords",
    header: "Success Records",
    render: (value) => (
      <div className="flex justify-center items-center pr-6">{value}</div>
    ),
  },
  {
    key: "errorRecords",
    header: "Error Records",
    render: (value) => (
      <div className="flex justify-center items-center  pr-6">{value}</div>
    ),
  },
];

// Recent Migrations Component - now accepts data as props
interface RecentMigrationsProps {
  migrationsData: any;
  isLoading: boolean;
  error: any;
}

const RecentMigrations: React.FC<RecentMigrationsProps> = ({
  migrationsData,
  isLoading,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("page")) || 1;
  const navigate = useNavigate();

  // Table actions configuration
  const routeMap: Record<string, string> = {
    consumers: "/cx/consumer-management/?page=1",
    meters: "/mx/device-management/inventory?tab=stock",
    "asset classifications":
      "/ax/settings/asset-classification?tab=classification",
    assets: "/ax/asset-registry/assets",
    facilities: "/ax/asset-registry/facilities",
    networks: "/ax/asset-registry/networks",
    systems: "/ax/asset-registry/systems",
    "system types": "/ax/settings/system-types",
    payments:
      "/cx/payments/?paymentMode=all&paymentReceivedStatus=all&paymentPayType=all&tab=allPayments",
    "consumer meter mapping":
      "/mx/device-management/meter?status=all&meterType=ALL&tab=all",
  };

  const actions: TableAction<DataHistoryItem>[] = [
    {
      label: "View Details",
      onClick: (migration) => {
        const targetType = migration.dataCategory?.toLowerCase(); // e.g. "consumer", "meter"
        const path = routeMap[targetType];

        if (path) {
          navigate(path);
        } else {
          toast({
            title: `No route mapped for:,${migration.dataCategory}`,
            description: ``,
          });
        }
      },
      icon: <Eye size={16} />,
    },
  ];

  const totalItems = migrationsData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem,
      endItem,
    }),
    [currentPage, totalPages, totalItems, startItem, endItem]
  );

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.search = encodeURIComponent(searchTerm.trim());
    } else {
      delete newParams.search;
    }

    newParams.page = "1";
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
  };

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="mb-6">
        <PageHeader
          title="Recent Migrations"
          subtitle="View details of your most recent data migrations and access the imported data"
        />
      </div>

      {/* Generic Table */}
      <GenericTable
        data={migrationsData?.result || []}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        actions={actions}
        searchPlaceholder="Search by Migration Name, Data Type..."
        searchParamName="search"
        searchDebounceMs={300}
        onSearch={handleSearch}
        pagination={pagination}
        onPageChange={handlePageChange}
        emptyMessage="No migrations found"
      />
    </div>
  );
};

// Main DataHistory component with stats
const DataHistory = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = Number(searchParams.get("page")) || 1;
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");

  const {
    data: migrationsData,
    isLoading,
    error,
  } = useDataHistory(currentPage, PAGE_SIZE, currentSearch);

  const { data: summaryData } = useMigrationHistorySummary();
  // Calculate stats from real data

  const dataOverviewItems = [
    {
      label: "Success Records",
      color: "text-blue-700",
      description:
        "Data that was successfully imported and is now available in your system",
    },
    {
      label: "Error Records",
      color: "text-blue-700",
      description:
        "Data that couldn't be processed due to validation issues or format problems",
    },
    {
      label: "View Data",
      color: "text-blue-700",
      description:
        "Access the actual migrated data to verify and analyze your imports",
    },
  ];

  // KPI Cards configuration using QuickStatsCard
  const migrationKpiCards = [
    {
      title: "Total Migrations",
      count: summaryData?.totalMigrations,
      trendSummary: "All data migration processes in the system",
      trendDirection: "up" as const,
      icon: <Database className="h-8 w-8 text-gray-500" />,
    },
    {
      title: "Completed",
      count: summaryData?.completedMigrations,
      trendSummary: "Successfully completed migration processes",
      trendDirection: "up" as const,
      icon: <CheckCircle className="h-8 w-8 text-green-500" />,
    },
    {
      title: "Total Records",
      count: summaryData?.totalRecords,
      trendSummary: "Total data records",
      trendDirection: "up" as const,
      icon: <BarChart3 className="h-8 w-8 text-blue-500" />,
    },
    {
      title: "Success Rate",
      count: `${summaryData?.successRate}%`,
      trendSummary: "Percentage of records successfully migrated",
      trendDirection: "up" as const,
      icon: <TrendingUp className="h-8 w-8 text-green-500" />,
    },
  ];

  return (
    <div className="min-h-screen">
      <div className="flex flex-col gap-5">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
            <Info size={20} className="text-blue-500" /> Data Migrations
            Overview
          </h2>
          <p className="text-sm text-blue-700 mt-1">
            Track and monitor all your data migration processes in one
            centralized location
          </p>
          <ul className="mt-3 text-sm text-gray-700 space-y-1">
            {dataOverviewItems.map((item, index) => (
              <li key={index}>
                <span className={`font-semibold ${item.color}`}>
                  • {item.label} :
                </span>{" "}
                <span className={`${item.color}`}>{item.description}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Quick Stats - Using loop with QuickStatsCard */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {migrationKpiCards.map((card, index) => (
            <QuickStatsCard
              key={index}
              title={card.title}
              count={card.count}
              trendSummary={card.trendSummary}
              trendDirection={card.trendDirection}
              icon={card.icon}
            />
          ))}
        </div>

        {/* Recent Migrations Table */}
        <RecentMigrations
          migrationsData={migrationsData}
          isLoading={isLoading}
          error={error}
        />
      </div>
    </div>
  );
};

export default DataHistory;
